import {
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
  ModalFooter,
  Button,
  Text,
  Image,
  useMediaQuery,
  theme,
  DrawerContent,
  DrawerHeader,
  Drawer,
  DrawerFooter,
  DrawerBody,
  DrawerOverlay,
  DrawerCloseButton,
} from '@everlywell/ui-kit';
import { Link } from 'gatsby';
import React from 'react';

export type ScreenerDataProps = {
  callToActionText: string;
  secondaryCallToActionText?: string;
  bodyImg?: any;
  callToActionUrl: string;
  title: string;
  description?: string;
  disclaimer?: string;
};

export type Props = {
  modalOpen: boolean;
  modalOpenCallback: (open: boolean) => void;
  screenerData: ScreenerDataProps;
};

const getDrawerModal = (isMobile: boolean) => {
  if (isMobile) {
    return {
      Container: Drawer,
      Overlay: DrawerOverlay,
      Content: DrawerContent,
      Header: DrawerHeader,
      Body: DrawerBody,
      Footer: DrawerFooter,
      CloseButton: DrawerCloseButton,
    };
  }

  return {
    Container: Modal,
    Overlay: ModalOverlay,
    Content: ModalContent,
    Header: ModalHeader,
    Body: ModalBody,
    Footer: ModalFooter,
    CloseButton: ModalCloseButton,
  };
};

const DrawerModal: React.FC<Props> = (props: Props) => {
  const { modalOpen, modalOpenCallback, screenerData } = props;

  const {
    callToActionText,
    secondaryCallToActionText = 'Go back',
    callToActionUrl,
    bodyImg,
    title,
    description,
    disclaimer,
  } = screenerData;

  const [isMobile] = useMediaQuery(`(max-width: ${theme.breakpoints.tablet})`);

  const { onClose } = useDisclosure();

  const modalOpenHandler = () => {
    modalOpenCallback(false);
    onClose();
  };

  const handleOnCTAClick = () => modalOpenHandler();

  const DrawerModal = getDrawerModal(isMobile);

  return modalOpen ? (
    <DrawerModal.Container
      onClose={modalOpenHandler}
      isOpen={modalOpen}
      size="xl"
      {...(isMobile ? { placement: 'bottom' } : {})}
    >
      <DrawerModal.Overlay
        zIndex={theme.zIndices.tooltip}
        sx={{
          '~ div + div > div': {
            zIndex: theme.zIndices.tooltip,
          },
        }}
      />
      <DrawerModal.Content
        backgroundColor="tints.white"
        borderRadius={isMobile ? '20px 20px 0 0' : '20px'}
      >
        <DrawerModal.Header
          fontSize="lg"
          color="tints.black"
          fontWeight="normal"
          {...(!isMobile ? { paddingTop: 6 } : {})}
        >
          {title}
          <DrawerModal.CloseButton
            backgroundColor="tints.cream"
            {...(!isMobile
              ? { transform: 'scale(.75)' }
              : { transform: 'scale(.9)' })}
          />
        </DrawerModal.Header>
        <DrawerModal.Body color="tints.darkGrey">
          {bodyImg && (
            <Image
              src={bodyImg}
              marginBottom={2}
              alt=""
              width="auto"
              height="auto"
            />
          )}
          {description && (
            <Text
              as="div"
              dangerouslySetInnerHTML={{ __html: description }}
              sx={{
                '> *:not(:last-child)': {
                  marginBottom: 4,
                },
                ul: {
                  marginLeft: 6,
                },
                '[data-component="ContentBox"]': {
                  backgroundColor: 'tints.lightCream',
                  padding: 3,
                  borderRadius: 'xl',
                  '> *:not(:last-child)': {
                    marginBottom: 4,
                  },
                },
              }}
            />
          )}

          <ModalFooter
            paddingX="0"
            display="flex"
            flexDirection="column"
            gap={4}
          >
            <Flex
              width="100%"
              paddingX="0"
              gap={4}
              sx={{
                '> a, > button': {
                  maxHeight: '44px',
                  fontSize: ['md', null, null, 'lg'],
                },
              }}
            >
              <Button
                variant="secondary"
                width="100%"
                onClick={modalOpenHandler}
              >
                {secondaryCallToActionText}
              </Button>
              <Button
                data-testid="cta"
                onClick={handleOnCTAClick}
                width="100%"
                {...(callToActionUrl ? { as: Link, to: callToActionUrl } : {})}
              >
                {callToActionText}
              </Button>
            </Flex>
            {disclaimer && (
              <Text size="sm" color="tints.darkGrey" textAlign="center">
                {disclaimer}
              </Text>
            )}
          </ModalFooter>
        </DrawerModal.Body>
      </DrawerModal.Content>
    </DrawerModal.Container>
  ) : null;
};

export default DrawerModal;
